import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Plus, Pencil, Trash2, Search, Loader2, CreditCard, MessageCircle, Clock, CalendarClock, Trophy } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirmDialog } from '@/components/useConfirmDialog';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  cpf: '',
  data_nascimento: '',
  sexo: '',
  posicao: '',
  responsavel_whatsapp: '',
  contato_preferencial: 'aluno',
  turma_ids: [],
  plano_id: '',
  dia_vencimento: '',
  desconto_mensal: '',
  tipo_primeira_cobranca: 'inscricao',
  valor_primeira_cobranca: '',
  status_primeira_mensalidade: 'aguardando_pagamento'
};

const categoriasFiltro = [
  { key: 'sub14', label: 'Sub 14' },
  { key: 'sub16', label: 'Sub 16' },
  { key: 'sub18', label: 'Sub 18' },
  { key: 'sub21', label: 'Sub 21' },
  { key: 'adulto', label: 'Adulto' },
];

function idadeNoAno(dataNascimento) {
  if (!dataNascimento) return null;

  const anoNascimento = Number(String(dataNascimento).slice(0, 4));

  if (!anoNascimento) return null;

  return new Date().getFullYear() - anoNascimento;
}

function categoriaDoAluno(aluno) {
  const idade = idadeNoAno(aluno.data_nascimento);

  if (idade == null) return null;
  if (idade <= 13) return 'sub14';
  if (idade <= 15) return 'sub16';
  if (idade <= 17) return 'sub18';
  if (idade <= 20) return 'sub21';

  return 'adulto';
}

function categoriaCompletaDoAluno(aluno) {
  const categoria = categoriaDoAluno(aluno);

  if (!categoria) return null;

  const sexo =
    aluno.sexo === 'masculino' || aluno.sexo === 'feminino'
      ? aluno.sexo
      : 'nao_informado';

  return `${sexo}:${categoria}`;
}

function formatWhatsApp(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10) return null;
  const num = digits.length <= 11 ? '55' + digits : digits;
  return num;
}

function buildWhatsAppLink(phone, message) {
  const num = formatWhatsApp(phone);
  if (!num) return null;
  return `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
}

function getContatoPreferencial(aluno) {
  const preferencia =
    aluno?.contato_preferencial === 'responsavel'
      ? 'responsavel'
      : 'aluno';

  const telefoneAluno = formatWhatsApp(aluno?.phone)
    ? aluno.phone
    : null;

  const telefoneResponsavel = formatWhatsApp(aluno?.responsavel_whatsapp)
    ? aluno.responsavel_whatsapp
    : null;

  if (preferencia === 'responsavel') {
    return telefoneResponsavel || telefoneAluno;
  }

  return telefoneAluno || telefoneResponsavel;
}

function msgLembreteAluno(nome) {
  return `Olá, ${nome}! Tudo bem?

Gostaria de lembrar que sua mensalidade vence em 2 dias.

Se precisar de qualquer informação ou apoio para o pagamento, estou à disposição.

Obrigado e bons treinos!`;
}

function msgVenceHojeAluno(nome) {
  return `Olá, ${nome}! Tudo bem?

Passando para lembrar que sua mensalidade vence hoje.

Peço, por gentileza, que realize o pagamento dentro do prazo para manter sua participação nos treinos normalmente.

Qualquer dúvida, estou à disposição.`;
}

export default function AlunosPage() {
  const { user } = useAuth();
  const isGestor = ['gestor', 'socio'].includes(user?.role);
  const canEdit = ['gestor', 'socio', 'financeiro'].includes(user?.role);
  const [alunos, setAlunos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroVencimento, setFiltroVencimento] = useState('todos');
  const [filtroTurma, setFiltroTurma] = useState('todas');
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [filtroPlano, setFiltroPlano] = useState('todos');
  const [filtroSituacao, setFiltroSituacao] = useState('todas');
  const [ordenacao, setOrdenacao] = useState('nome-az');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [planoDialog, setPlanoDialog] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [planoId, setPlanoId] = useState('');
  const [planoDesconto, setPlanoDesconto] = useState('');
  const [savingPlano, setSavingPlano] = useState(false);
  const [perfilAluno, setPerfilAluno] = useState(null);
  const [medalhaDialog, setMedalhaDialog] = useState(null);
  const [medalhaForm, setMedalhaForm] = useState({
    nome: 'Kartal Primeira Geração',
    icone: '🦅',
    descricao: 'Atleta pertencente à primeira geração oficial da Kartal.',
    xp: 100
  });
  const [savingMedalha, setSavingMedalha] = useState(false);

  useEffect(() => {
    const fetches = [api.get('/gestor/alunos')];
    if (user?.role !== 'financeiro') fetches.push(api.get('/gestor/turmas'));
    if (user?.role !== 'professor') fetches.push(api.get('/gestor/planos'));

    Promise.all(fetches).then(results => {
      setAlunos(results[0].data);
      if (user?.role !== 'financeiro' && results[1]) setTurmas(results[1].data);
      const planoIdx = user?.role === 'financeiro' ? 1 : (user?.role === 'professor' ? 1 : 2);
      if (results[planoIdx]) setPlanos(results[planoIdx].data);
    }).catch(() => toast.error('Erro ao carregar')).finally(() => setLoading(false));
  }, [user?.role]);

  const refresh = async () => { const r = await api.get('/gestor/alunos'); setAlunos(r.data); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/gestor/alunos/${editing.id}`, form);
        toast.success('Aluno atualizado');
      } else {
        const payload = {
          ...form,
          valor_primeira_cobranca:
            form.valor_primeira_cobranca === '' ||
            form.valor_primeira_cobranca == null
              ? null
              : Number(form.valor_primeira_cobranca)
        };

        await api.post('/gestor/alunos', payload);
        toast.success('Aluno criado');
      }
      setDialogOpen(false);
      setEditing(null);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!await confirm('Remover Aluno', 'Tem certeza que deseja remover este aluno?')) return;
    try { await api.delete(`/gestor/alunos/${id}`); toast.success('Aluno removido'); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const openCreate = () => { setEditing(null); setForm(initialForm); setDialogOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, email: a.email, phone: a.phone || '', cpf: a.cpf || '', data_nascimento: a.data_nascimento || '',posicao: a.posicao || '', sexo: a.sexo || '', responsavel_whatsapp: a.responsavel_whatsapp || '', contato_preferencial: a.contato_preferencial || 'aluno', turma_ids: a.turma_ids || [], plano_id: a.plano_id || '', dia_vencimento: a.dia_vencimento || '', desconto_mensal: a.desconto_mensal ?? '', status: a.status || 'ativo' });
    setDialogOpen(true);
  };

  const openPlanoDialog = (a) => {
    setPlanoDialog(a);
    setPlanoId(a.plano_id || '');
    setPlanoDesconto(a.desconto_mensal ?? '');
  };
  const vincularPlano = async () => {
    if (!planoId) { toast.error('Selecione um plano'); return; }
    setSavingPlano(true);
    try {
      await api.put(`/gestor/alunos/${planoDialog.id}/vincular-plano`, {
        plano_id: planoId,
        desconto_mensal: Math.max(Number(planoDesconto || 0), 0)
      });
      toast.success('Plano vinculado ao aluno!');
      setPlanoDialog(null);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setSavingPlano(false);
  };

  const removerMedalha = async (medalha) => {
    if (!medalhaDialog) return;

    try {
      await api.delete(`/gestor/alunos/${medalhaDialog.id}/medalhas`, {
        data: medalha
      });

      toast.success('Medalha removida com sucesso!');

      setMedalhaDialog({
        ...medalhaDialog,
        medalhas_especiais: (medalhaDialog.medalhas_especiais || []).filter(m => m.medalha_id !== medalha.medalha_id)
      });

      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const concederMedalha = async () => {
    if (!medalhaDialog) return;

    setSavingMedalha(true);

    try {
      await api.post(`/gestor/alunos/${medalhaDialog.id}/medalhas`, medalhaForm);
      toast.success('Medalha concedida com sucesso!');
      setMedalhaDialog(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }

    setSavingMedalha(false);
  };

  const limparFiltros = () => {
    setSearch('');
    setFiltroTurma('todas');
    setFiltroCategoria('todas');
    setFiltroPlano('todos');
    setFiltroVencimento('todos');
    setFiltroSituacao('todas');
    setOrdenacao('nome-az');
  };

  const filtrosAtivos =
    Number(search.trim() !== '') +
    Number(filtroTurma !== 'todas') +
    Number(filtroCategoria !== 'todas') +
    Number(filtroPlano !== 'todos') +
    Number(filtroVencimento !== 'todos') +
    Number(filtroSituacao !== 'todas');

  const filtered = alunos
    .filter(a => {
      const termo = search.trim().toLowerCase();

      const matchesSearch =
        termo === '' ||
        a.name?.toLowerCase().includes(termo) ||
        a.email?.toLowerCase().includes(termo) ||
        a.phone?.toLowerCase().includes(termo) ||
        a.cpf?.toLowerCase().includes(termo);

      const turmaIds = Array.isArray(a.turma_ids)
        ? a.turma_ids.map(String)
        : a.turma_id
          ? [String(a.turma_id)]
          : [];

      const matchesTurma =
        filtroTurma === 'todas' ||
        turmaIds.includes(String(filtroTurma));

      const matchesCategoria =
        filtroCategoria === 'todas' ||
        categoriaCompletaDoAluno(a) === filtroCategoria;

      const matchesPlano =
        filtroPlano === 'todos' ||
        String(a.plano_id || '') === String(filtroPlano);

      const matchesVencimento =
        filtroVencimento === 'todos' ||
        String(a.dia_vencimento || '') === filtroVencimento;

      const statusAluno = a.status || 'ativo';

      const matchesSituacao =
        filtroSituacao === 'todas' ||
        statusAluno === filtroSituacao;

      return (
        matchesSearch &&
        matchesTurma &&
        matchesCategoria &&
        matchesPlano &&
        matchesVencimento &&
        matchesSituacao
      );
    })
    .sort((a, b) => {
      if (ordenacao === 'nome-za') {
        return (b.name || '').localeCompare(a.name || '', 'pt-BR');
      }

      if (ordenacao === 'vencimento') {
        return Number(a.dia_vencimento || 99) - Number(b.dia_vencimento || 99);
      }

      if (ordenacao === 'idade') {
        return (idadeNoAno(a.data_nascimento) || 0) -
          (idadeNoAno(b.data_nascimento) || 0);
      }

      if (ordenacao === 'mais-novos') {
        const dataA = new Date(a.created_at || a.data_cadastro || 0).getTime();
        const dataB = new Date(b.created_at || b.data_cadastro || 0).getTime();

        return dataB - dataA;
      }

      return (a.name || '').localeCompare(b.name || '', 'pt-BR');
    });

  return (
    <TooltipProvider>
    <div className="space-y-6 animate-fadeIn" data-testid="alunos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">ALUNOS</h1>
        {isGestor && <Button onClick={openCreate} className="bg-[var(--cor-primaria,#007AFF)] hover:opacity-90" data-testid="add-aluno-button"><Plus className="w-4 h-4 mr-2" /> Novo Aluno</Button>}
      </div>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                className="pl-10 bg-[#0A0A0A] border-[#27272A]"
                placeholder="Buscar por nome, e-mail, telefone ou CPF..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="alunos-search"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
              <Select value={filtroTurma} onValueChange={setFiltroTurma}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                  <SelectValue placeholder="Turma" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as turmas</SelectItem>
                  {turmas.map(t => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as categorias</SelectItem>

                  {['feminino', 'masculino'].map(sexo =>
                    categoriasFiltro.map(categoria => (
                      <SelectItem
                        key={`${sexo}:${categoria.key}`}
                        value={`${sexo}:${categoria.key}`}
                      >
                        {sexo === 'feminino' ? 'Feminino' : 'Masculino'} — {categoria.label}
                      </SelectItem>
                    ))
                  )}

                  {categoriasFiltro.map(categoria => (
                    <SelectItem
                      key={`nao_informado:${categoria.key}`}
                      value={`nao_informado:${categoria.key}`}
                    >
                      Sexo não informado — {categoria.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filtroPlano} onValueChange={setFiltroPlano}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                  <SelectValue placeholder="Plano" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os planos</SelectItem>
                  {planos.map(p => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filtroVencimento} onValueChange={setFiltroVencimento}>
                <SelectTrigger
                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                  data-testid="filtro-vencimento-alunos"
                >
                  <SelectValue placeholder="Vencimento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os vencimentos</SelectItem>
                  {[...Array(31)].map((_, i) => {
                    const dia = String(i + 1);

                    return (
                      <SelectItem key={dia} value={dia}>
                        Vencimento dia {dia}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              <Select value={filtroSituacao} onValueChange={setFiltroSituacao}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                  <SelectValue placeholder="Situação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as situações</SelectItem>
                  <SelectItem value="ativo">Ativos</SelectItem>
                  <SelectItem value="inativo">Inativos</SelectItem>
                  <SelectItem value="bloqueado">Bloqueados</SelectItem>
                </SelectContent>
              </Select>

              <Select value={ordenacao} onValueChange={setOrdenacao}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nome-az">Nome: A–Z</SelectItem>
                  <SelectItem value="nome-za">Nome: Z–A</SelectItem>
                  <SelectItem value="vencimento">Dia de vencimento</SelectItem>
                  <SelectItem value="idade">Menor idade primeiro</SelectItem>
                  <SelectItem value="mais-novos">Cadastros mais novos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm text-zinc-400">
                Mostrando{' '}
                <span className="font-semibold text-white">{filtered.length}</span>
                {' '}de{' '}
                <span className="font-semibold text-white">{alunos.length}</span>
                {' '}alunos
                {filtrosAtivos > 0 && (
                  <span className="ml-2 text-[var(--cor-primaria,#007AFF)]">
                    · {filtrosAtivos} {filtrosAtivos === 1 ? 'filtro ativo' : 'filtros ativos'}
                  </span>
                )}
              </p>

              {filtrosAtivos > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={limparFiltros}
                  className="border-[#3F3F46] text-zinc-300 hover:bg-[#27272A] hover:text-white"
                >
                  Limpar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#27272A] hover:bg-transparent">
                <TableHead className="text-zinc-400">Nome</TableHead>
                <TableHead className="text-zinc-400">Email</TableHead>
                <TableHead className="text-zinc-400">Telefone</TableHead>
                <TableHead className="text-zinc-400">Plano</TableHead>
                <TableHead className="text-zinc-400">Situacao</TableHead>
                <TableHead className="text-zinc-400 text-right">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">Carregando...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">Nenhum aluno encontrado</TableCell></TableRow>
              ) : filtered.map(a => {
                const contatoPreferencial = getContatoPreferencial(a);
                const hasPhone = !!formatWhatsApp(contatoPreferencial);

                return (
                <TableRow key={a.id} className="border-[#27272A] hover:bg-[#1C1C1E]" data-testid={`aluno-row-${a.id}`}>
                  <TableCell className="text-white font-medium">
                    <button
                      type="button"
                      onClick={() => setPerfilAluno(a)}
                      className="hover:text-[var(--cor-primaria,#007AFF)] hover:underline text-left"
                    >
                      {a.name}
                    </button>
                  </TableCell>
                  <TableCell className="text-zinc-400">{a.email}</TableCell>
                  <TableCell className="text-zinc-400">{a.phone || '-'}</TableCell>
                  <TableCell>
                    {a.plano_nome ? (
                      <Badge className="bg-[var(--cor-primaria,#007AFF)]/10 text-[var(--cor-primaria,#007AFF)] border-[var(--cor-primaria,#007AFF)]/20 cursor-pointer" onClick={() => canEdit && openPlanoDialog(a)} data-testid={`aluno-plano-${a.id}`}>
                        {a.plano_nome}
                      </Badge>
                    ) : canEdit ? (
                      <Button size="sm" variant="ghost" className="text-amber-400 hover:text-amber-300 text-xs h-6 px-2" onClick={() => openPlanoDialog(a)} data-testid={`vincular-plano-aluno-${a.id}`}>
                        <CreditCard className="w-3 h-3 mr-1" /> Vincular
                      </Button>
                    ) : (
                      <span className="text-xs text-zinc-500">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {a.inadimplente ? (
                      <Badge variant="destructive" className="bg-red-500/10 text-red-400 border-red-500/20" data-testid={`aluno-inadimplente-${a.id}`}>Inadimplente</Badge>
                    ) : (
                      <Badge className="bg-green-500/10 text-green-400 border-green-500/20">Em dia</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="ghost" className={hasPhone ? 'text-green-400 hover:text-green-300' : 'text-zinc-600 cursor-not-allowed'} disabled={!hasPhone} onClick={() => hasPhone && window.open(buildWhatsAppLink(contatoPreferencial, msgLembreteAluno(a.name)), '_blank')} data-testid={`wa-lembrete-aluno-${a.id}`}>
                          <Clock className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-[#141414] border-[#27272A]"><p className="text-xs">Lembrar (2 dias)</p></TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="ghost" className={hasPhone ? 'text-amber-400 hover:text-amber-300' : 'text-zinc-600 cursor-not-allowed'} disabled={!hasPhone} onClick={() => hasPhone && window.open(buildWhatsAppLink(contatoPreferencial, msgVenceHojeAluno(a.name)), '_blank')} data-testid={`wa-vencehoje-aluno-${a.id}`}>
                          <CalendarClock className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-[#141414] border-[#27272A]"><p className="text-xs">Vence hoje</p></TooltipContent>
                    </Tooltip>
                    {isGestor && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setMedalhaDialog(a)} className="text-yellow-400 hover:text-yellow-300" title="Conceder medalha" data-testid={`medalha-aluno-${a.id}`}><Trophy className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(a)} className="text-zinc-400 hover:text-white" data-testid={`edit-aluno-${a.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(a.id)} className="text-zinc-400 hover:text-red-400" data-testid={`delete-aluno-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">{editing ? 'Editar Aluno' : 'Novo Aluno'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required data-testid="aluno-form-name" /></div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required data-testid="aluno-form-email" /></div>
            {!editing && <p className="text-xs text-zinc-500">Senha padrao: 12345678</p>}
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Telefone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" placeholder="(11) 99999-9999" data-testid="aluno-form-phone" /></div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Data de nascimento</Label><Input type="date" value={form.data_nascimento || ''} onChange={e => setForm({...form, data_nascimento: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" data-testid="aluno-form-data-nascimento" /></div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Sexo</Label>
              <Select value={form.sexo || 'nao_informado'} onValueChange={v => setForm({...form, sexo: v === 'nao_informado' ? '' : v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="aluno-form-sexo">
                  <SelectValue placeholder="Selecionar sexo" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#27272A]">
                  <SelectItem value="nao_informado">Não informado</SelectItem>
                  <SelectItem value="masculino">Masculino</SelectItem>
                  <SelectItem value="feminino">Feminino</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                WhatsApp do responsável
              </Label>
              <Input
                value={form.responsavel_whatsapp || ''}
                onChange={e => setForm({
                  ...form,
                  responsavel_whatsapp: e.target.value
                })}
                className="bg-[#0A0A0A] border-[#27272A]"
                placeholder="(99) 99999-9999"
                data-testid="aluno-form-responsavel-whatsapp"
              />
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                Enviar cobranças e lembretes para
              </Label>

              <Select
                value={form.contato_preferencial || 'aluno'}
                onValueChange={valor =>
                  setForm({
                    ...form,
                    contato_preferencial: valor
                  })
                }
              >
                <SelectTrigger
                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                  data-testid="aluno-form-contato-preferencial"
                >
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="aluno">
                    WhatsApp do aluno
                  </SelectItem>

                  <SelectItem value="responsavel">
                    WhatsApp do responsável
                  </SelectItem>
                </SelectContent>
              </Select>

              <p className="text-xs text-zinc-500 mt-1">
                Se o número escolhido estiver vazio, o sistema usará
                automaticamente o outro contato disponível.
              </p>
            </div>
<div>
  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Dia de Vencimento</Label>
  <Input
    type="number"
    min="1"
    max="31"
    value={form.dia_vencimento || ''}
    onChange={e => setForm({...form, dia_vencimento: e.target.value})}
    className="bg-[#0A0A0A] border-[#27272A]"
    placeholder="Ex: 10"
    data-testid="aluno-form-dia-vencimento"
  />
<div>
    <Label className="text-zinc-400 text-xs uppercase tracking-wider">
        Posição
    </Label>

    <select
        value={form.posicao || ""}
        onChange={(e) => setForm({ ...form, posicao: e.target.value })}
        className="w-full rounded-md border border-[#27272A] bg-[#0A0A0A] p-2 text-white"
    >
        <option value="">Selecione</option>
        <option value="levantador">Levantador</option>
        <option value="ponteiro">Ponteiro</option>
        <option value="central">Central</option>
        <option value="oposto">Oposto</option>
        <option value="libero">Líbero</option>
    </select>
</div>
</div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">CPF</Label><Input value={form.cpf} onChange={e => setForm({...form, cpf: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" /></div>
            {turmas.length > 0 && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Turma</Label>
                <Select value={form.turma_ids?.[0] || 'none'} onValueChange={v => setForm({...form, turma_ids: v === 'none' ? [] : [v]})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]"><SelectValue placeholder="Selecionar turma" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#27272A]">
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {turmas.map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {planos.length > 0 && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Plano</Label>
                <Select
                  value={form.plano_id || 'none'}
                  onValueChange={v => {
                    if (v === 'none') {
                      setForm({
                        ...form,
                        plano_id: '',
                        valor_primeira_cobranca: ''
                      });
                      return;
                    }

                    const planoSelecionado = planos.find(p => p.id === v);
                    const valorPlano = Number(planoSelecionado?.valor || 0);
                    const desconto = Math.max(Number(form.desconto_mensal || 0), 0);
                    const valorInicial = Math.max(valorPlano - Math.min(desconto, valorPlano), 0);

                    setForm({
                      ...form,
                      plano_id: v,
                      valor_primeira_cobranca: valorInicial.toFixed(2)
                    });
                  }}
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="aluno-form-plano"><SelectValue placeholder="Selecionar plano" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#27272A]">
                    <SelectItem value="none">Sem plano</SelectItem>
                    {planos.map(p => <SelectItem key={p.id} value={p.id}>{p.nome} - R$ {(p.valor || 0).toFixed(2)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.plano_id && (
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                  Desconto mensal (R$)
                </Label>

                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.desconto_mensal}
                  onChange={e => setForm({
                    ...form,
                    desconto_mensal: e.target.value
                  })}
                  className="bg-[#0A0A0A] border-[#27272A]"
                  placeholder="0,00"
                  data-testid="aluno-form-desconto"
                />

                {(() => {
                  const plano = planos.find(p => p.id === form.plano_id);
                  if (!plano) return null;

                  const valorPlano = Number(plano.valor || 0);
                  const descontoInformado = Math.max(
                    Number(form.desconto_mensal || 0),
                    0
                  );
                  const descontoAplicado = Math.min(
                    descontoInformado,
                    valorPlano
                  );
                  const valorFinal = Math.max(
                    valorPlano - descontoAplicado,
                    0
                  );

                  return (
                    <div className="rounded-md border border-[#27272A] bg-[#0A0A0A] p-3 text-sm">
                      <div className="flex justify-between text-zinc-400">
                        <span>Valor do plano</span>
                        <span>
                          R$ {valorPlano.toFixed(2).replace('.', ',')}
                        </span>
                      </div>

                      <div className="mt-1 flex justify-between text-zinc-400">
                        <span>Desconto</span>
                        <span>
                          - R$ {descontoAplicado.toFixed(2).replace('.', ',')}
                        </span>
                      </div>

                      <div className="mt-2 border-t border-[#27272A] pt-2 flex justify-between">
                        <span className="font-medium text-white">
                          Mensalidade
                        </span>
                        <span className="font-semibold text-white">
                          R$ {valorFinal.toFixed(2).replace('.', ',')}
                        </span>
                      </div>

                      <p className="mt-2 text-xs text-zinc-500">
                        O desconto será aplicado às próximas mensalidades geradas.
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}


            {!editing && form.plano_id && (
              <div className="space-y-4 rounded-lg border border-[#27272A] bg-[#0A0A0A]/40 p-4">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                    Tipo da primeira cobrança
                  </Label>

                  <select
                    value={form.tipo_primeira_cobranca || 'inscricao'}
                    onChange={e => setForm({
                      ...form,
                      tipo_primeira_cobranca: e.target.value
                    })}
                    className="w-full rounded-md border border-[#27272A] bg-[#0A0A0A] p-2 text-white"
                  >
                    <option value="inscricao">
                      Inscrição — não entra no repasse do parceiro
                    </option>
                    <option value="mensalidade">
                      Mensalidade — entra normalmente no repasse
                    </option>
                  </select>
                </div>

                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                    Valor da primeira cobrança (R$)
                  </Label>

                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.valor_primeira_cobranca ?? ''}
                    onChange={e => setForm({
                      ...form,
                      valor_primeira_cobranca: e.target.value
                    })}
                    className="bg-[#0A0A0A] border-[#27272A]"
                    placeholder="0,00"
                  />

                  <p className="mt-1 text-xs text-zinc-500">
                    Este valor vale somente para a primeira cobrança. As próximas mensalidades continuam usando o valor do plano e o desconto mensal.
                  </p>
                </div>

                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                    Status da primeira cobrança
                  </Label>

                <Select
                  value={form.status_primeira_mensalidade || 'aguardando_pagamento'}
                  onValueChange={valor =>
                    setForm({
                      ...form,
                      status_primeira_mensalidade: valor
                    })
                  }
                >
                  <SelectTrigger
                    className="bg-[#0A0A0A] border-[#27272A]"
                    data-testid="aluno-form-status-primeira-mensalidade"
                  >
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent className="bg-[#141414] border-[#27272A]">
                    <SelectItem value="aguardando_pagamento">
                      Aguardando pagamento
                    </SelectItem>
                    <SelectItem value="pago">
                      Pago
                    </SelectItem>
                  </SelectContent>
                </Select>

                <p className="text-xs text-zinc-500 mt-1">
                  Define como a primeira cobrança será criada.
                </p>
                </div>
              </div>
            )}

            {editing && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#27272A]">
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                    <SelectItem value="trancado">Trancado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-zinc-400">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[var(--cor-primaria,#007AFF)] hover:opacity-90" data-testid="aluno-form-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Salvar' : 'Criar')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vincular Plano Dialog */}
      <Dialog open={!!planoDialog} onOpenChange={() => setPlanoDialog(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">Vincular Plano</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">Aluno: <span className="text-white font-medium">{planoDialog?.name}</span></p>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Plano</Label>
              <Select value={planoId || 'none'} onValueChange={v => setPlanoId(v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="vincular-plano-aluno-select">
                  <SelectValue placeholder="Selecione um plano" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#27272A]">
                  <SelectItem value="none">Sem plano</SelectItem>
                  {planos.map(p => <SelectItem key={p.id} value={p.id}>{p.nome} - R$ {(p.valor || 0).toFixed(2)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {planoId && (() => {
              const plano = planos.find(p => p.id === planoId);
              if (!plano) return null;

              const valorPlano = Number(plano.valor || 0);
              const descontoInformado = Math.max(
                Number(planoDesconto || 0),
                0
              );
              const descontoAplicado = Math.min(
                descontoInformado,
                valorPlano
              );
              const valorFinal = Math.max(
                valorPlano - descontoAplicado,
                0
              );

              return (
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                    Desconto mensal (R$)
                  </Label>

                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={planoDesconto}
                    onChange={e => setPlanoDesconto(e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A]"
                    placeholder="0,00"
                    data-testid="vincular-plano-desconto"
                  />

                  <div className="rounded-md border border-[#27272A] bg-[#0A0A0A] p-3 text-sm">
                    <div className="flex justify-between text-zinc-400">
                      <span>Plano</span>
                      <span>
                        R$ {valorPlano.toFixed(2).replace('.', ',')}
                      </span>
                    </div>

                    <div className="mt-1 flex justify-between text-zinc-400">
                      <span>Desconto</span>
                      <span>
                        - R$ {descontoAplicado.toFixed(2).replace('.', ',')}
                      </span>
                    </div>

                    <div className="mt-2 border-t border-[#27272A] pt-2 flex justify-between">
                      <span className="font-medium text-white">
                        Mensalidade
                      </span>
                      <span className="font-semibold text-white">
                        R$ {valorFinal.toFixed(2).replace('.', ',')}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}

            <DialogFooter>
              <Button variant="ghost" onClick={() => setPlanoDialog(null)} className="text-zinc-400">Cancelar</Button>
              <Button onClick={vincularPlano} disabled={savingPlano} className="bg-[var(--cor-primaria,#007AFF)] hover:opacity-90" data-testid="vincular-plano-aluno-submit">
                {savingPlano ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vincular'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>

    <Dialog open={!!perfilAluno} onOpenChange={(open) => !open && setPerfilAluno(null)}>
      <DialogContent className="bg-[#141414] border-[#27272A] text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl tracking-tight uppercase">
            Perfil do Aluno
          </DialogTitle>
        </DialogHeader>

        {perfilAluno && (
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-[#0A0A0A] border border-[#27272A] overflow-hidden flex items-center justify-center">
                {perfilAluno.avatar_url ? (
                  <img src={perfilAluno.avatar_url} alt={perfilAluno.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold text-zinc-500">
                    {perfilAluno.name?.charAt(0) || '?'}
                  </span>
                )}
              </div>

              <div>
                <h3 className="text-xl font-bold text-white">{perfilAluno.name || 'Não informado'}</h3>
                <p className="text-sm text-zinc-500">{perfilAluno.email || 'Não informado'}</p>
                <Badge className={perfilAluno.status === 'ativo' ? 'bg-green-500/10 text-green-400 border-green-500/20 mt-2' : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20 mt-2'}>
                  {perfilAluno.status || 'Não informado'}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Data de nascimento</p>
                <p className="text-sm text-white mt-1">{perfilAluno.data_nascimento || 'Não informado'}</p>
              </div>

              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">WhatsApp do aluno</p>
                <p className="text-sm text-white mt-1">{perfilAluno.phone || 'Não informado'}</p>
              </div>

              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">WhatsApp do responsável</p>
                <p className="text-sm text-white mt-1">{perfilAluno.responsavel_whatsapp || perfilAluno.whatsapp_responsavel || 'Não informado'}</p>
              </div>

              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Turma</p>
                <p className="text-sm text-white mt-1">{perfilAluno.turma_nome || perfilAluno.turmas_nome || 'Não informado'}</p>
              </div>

              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Plano</p>
                <p className="text-sm text-white mt-1">{perfilAluno.plano_nome || 'Não informado'}</p>
              </div>

              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Dia de vencimento</p>
                <p className="text-sm text-white mt-1">{perfilAluno.dia_vencimento || 'Não informado'}</p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    <Dialog open={!!medalhaDialog} onOpenChange={() => setMedalhaDialog(null)}>
      <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">
            Conceder Medalha
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-zinc-400">
            Aluno: <span className="text-white font-semibold">{medalhaDialog?.name}</span>
          </p>

          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Medalhas atuais</Label>

            {(medalhaDialog?.medalhas_especiais || []).length === 0 ? (
              <p className="text-xs text-zinc-500">Nenhuma medalha especial concedida.</p>
            ) : (
              <div className="space-y-2">
                {(medalhaDialog?.medalhas_especiais || []).map((m, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#0A0A0A] border border-[#27272A] rounded-xl px-3 py-2">
                    <div>
                      <p className="text-sm text-white">{m.icone} {m.nome}</p>
                      <p className="text-xs text-zinc-500">{m.xp || 0} XP</p>
                    </div>

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removerMedalha(m)}
                      className="text-red-400 hover:text-red-300"
                    >
                      Remover
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome da medalha</Label>
            <Input
              value={medalhaForm.nome}
              onChange={e => setMedalhaForm({...medalhaForm, nome: e.target.value})}
              className="bg-[#0A0A0A] border-[#27272A]"
            />
          </div>

          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Ícone</Label>
            <Input
              value={medalhaForm.icone}
              onChange={e => setMedalhaForm({...medalhaForm, icone: e.target.value})}
              className="bg-[#0A0A0A] border-[#27272A]"
            />
          </div>

          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Descrição</Label>
            <Input
              value={medalhaForm.descricao}
              onChange={e => setMedalhaForm({...medalhaForm, descricao: e.target.value})}
              className="bg-[#0A0A0A] border-[#27272A]"
            />
          </div>

          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">XP concedido</Label>
            <Input
              type="number"
              min="0"
              value={medalhaForm.xp}
              onChange={e => setMedalhaForm({...medalhaForm, xp: Number(e.target.value)})}
              className="bg-[#0A0A0A] border-[#27272A]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setMedalhaDialog(null)} className="text-zinc-400">
            Cancelar
          </Button>
          <Button onClick={concederMedalha} disabled={savingMedalha} className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
            {savingMedalha ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Conceder medalha'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <ConfirmDialog />
    </TooltipProvider>
  );
}
